"use client";

import { GoogleOAuthProvider } from "@react-oauth/google";
import React from "react";
import { useAuthStore } from "./store/authstore";
import { initRemoteConfigValues } from "./lib/initRemoteConfigValues";
import { useRemoteConfigStore } from "./store/remoteConfigStore";
import BonusPopup from "./component/coinpopup/BonusCoin";
import { useBonusPopupStore, useCoinStore, logoutClearAllRewardStores } from "./store/coinstore";
import { addCoinData } from "./utils/coinApi";
import { useRouter } from "next/navigation";
import { installDevtoolsGuard } from "./utils/devtoolsGuard";

//941230218987-pk42s1dmad8fg1i2a4kk68gj2drh74bd.apps.googleusercontent.com
const GOOGLE_CLIENT_ID = "202798486103-2elgcak04c3n6l23a9bpocd41kvcah74.apps.googleusercontent.com";
const FACEBOOK_APP_ID = "562949939483399"; //562949939483399 1014078386496635

export default function ClientProviders({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const config = useRemoteConfigStore((s) => s.config);
  const showBonusPopup = useBonusPopupStore((s) => s.showBonusPopup);
  const router = useRouter();

  const getStarted = async () => {
    if (!user) return; // Guard: don't write to coin store if no user
    useBonusPopupStore.getState().hide();
    const res = await addCoinData({ type: "add", welcome_bonus: 1,coins: config.welcomeBonus }, "firstUser");
    // console.log('add coin response:', res);
    if(res && res.ErrorCode == 1){
     const coins = useCoinStore.getState();
     coins.setCoinBalance({ welcomeBonus: "1", totalCoins: (coins.coinBalance.totalCoins + config.welcomeBonus) });
     router.push('/rewards?init=welcome');
    }
  }
  // Initialize Firebase Remote Config on mount
  React.useEffect(() => {
    initRemoteConfigValues();
  }, []);

  // Block devtools entry points if not in allowed origins (dev/test environments)
  React.useEffect(() => {
    const cleanup = installDevtoolsGuard({
      message: "Developer tools are disabled on this site.",
      // Add allowed origins here for dev/test environments
      // In production, leave empty or remove this array to block all devtools
      allowedOrigins: [
        'http://localhost:*',
        'https://6pphgq58-3000.inc1.devtunnels.ms/'
      ],
    });
    return cleanup;
  }, []);

  // Clear reward stores on boot or when user logs out
  React.useEffect(() => {
    if (!user) {
      try {
        logoutClearAllRewardStores();
        localStorage.removeItem('_coinstore');
        localStorage.removeItem('_bonusmodal');
      } catch {}
    }
  }, [user]);

  // Fetch FCM token only when permission is already granted (no prompts on load)
  React.useEffect(() => {
    const fetchTokenIfPermitted = async () => {
      if (typeof window === 'undefined') return;
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return; // do nothing unless already granted

      try {
        const { getFcmToken } = await import('./lib/firebaseMessaging');
        const fcmToken = await getFcmToken();
        if (fcmToken) {
          localStorage.setItem('fcm_token', fcmToken);
          localStorage.setItem('fcm_token_time', Date.now().toString());
          // console.log('✓ FCM token cached to localStorage (auto-refresh)');
        }
      } catch (err) {
        console.warn('FCM token refresh skipped:', err);
      }
    };

    fetchTokenIfPermitted();

    // Refresh token every 7 days if permission stays granted
    const interval = setInterval(fetchTokenIfPermitted, 7 * 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Facebook SDK initialization
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      type FBInitOptions = { appId: string; cookie: boolean; xfbml: boolean; version: string; status: boolean };
      const win = window as Window & { fbAsyncInit?: () => void; FB?: { init?: (opts: FBInitOptions) => void } };

      // Set up fbAsyncInit before script loads
      win.fbAsyncInit = function () {
        // call FB.init if available
        win.FB?.init?.({
          appId: FACEBOOK_APP_ID,
          cookie: true,
          xfbml: false,
          version: 'v19.0',
          status: true,
        });
      };

      // Load the SDK script if not already present
      if (!document.getElementById('facebook-jssdk')) {
        const js = document.createElement('script');
        js.id = 'facebook-jssdk';
        js.async = true;
        js.defer = true;
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        document.head.appendChild(js);
      }
    }
  }, []);

  // Conditional AdSense script loader: we only inject the global adsbygoogle script
  // when the user is not a logged-in premium user. This prevents premium users from
  // loading ads or the ad script.
  React.useEffect(() => {
    const scriptId = 'adsense-client-script';

    const isPremium = Boolean(user?.premium_data?.premium === "1" || user?.premiumpurchase === "1");

    if (!isPremium) {
      // If user is not premium (including not logged in), ensure ad script is present
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1374032065732962`;
        script.crossOrigin = 'anonymous';
        setTimeout(() => {
          document.head.appendChild(script);
        }, 3000);
      }
    } else {
      // If the user is premium, remove the script we may have added earlier to avoid loading ads
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    }

    // No cleanup needed beyond removal handled above
  }, [user]);


  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {children}
      {showBonusPopup && <BonusPopup onGetStarted={async () => await getStarted()} amount={config.welcomeBonus}/>}
    </GoogleOAuthProvider>
  );
}